'use client';

import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Popover } from '@/shared/components';
import { Catalog as ICatalog, CatalogElements } from '@/types/search-result';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { SEARCH_RESULT_GROUP_JOIN_KEY, SEARCH_RESULT_ITEM_JOIN_KEY } from '@/app/utils/constant';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
interface IProps {
  catalogs: ICatalog[];
  filters?: string;
  onFiltersChange: (filters: string) => void;
  onCatalogChange?: (gpt: string) => void;
}

interface CatalogAccordionsProps {
  catalog: ICatalog;
  groupArray: string[];
  onFiltersChange: (filters: string) => void;
  onCatalogChange?: (gpt: string) => void;
}

interface CatalogProps {
  sub: CatalogElements;
  parentName: string;
  groupAndItemInfo: any;
  setParamsUrl: (groupFilters: string[]) => void;
  onCatalogChange?: (gpt: string) => void;
  clickClearAll: boolean;
  setClickClearAll: (value: boolean) => void;
}

export default function Catalogs({ catalogs, filters, onFiltersChange, onCatalogChange }: IProps) {
  const [groupArray, setGroupArray] = useState<string[]>([]);
  const [catalogsState, setCatalogsState] = useState<ICatalog[]>([]);
  const { t } = useLang(NAMESPACE.search);
  const inputFilterList = filters?.split(SEARCH_RESULT_GROUP_JOIN_KEY)?.filter(Boolean) ?? [];

  useEffect(() => {
    if (Array.isArray(catalogs)) {
      const groupArray = [];
      catalogs.forEach((catalog) => {
        const filterList = [];
        const filter =
          inputFilterList
            ?.find((inputFilter) => inputFilter.includes(`${catalog.name}:`))
            ?.replace(`${catalog.name}:`, '')
            ?.split(SEARCH_RESULT_ITEM_JOIN_KEY)
            ?.filter(Boolean) ?? [];
        catalog.refinementElements.forEach((refinementElement) => {
          if (refinementElement.multiSelectOr) {
            refinementElement.facetFilter = refinementElement.facetFilter.replace(`${catalog.name}:`, '');
          }

          if (refinementElement.selected || filter.includes(refinementElement.facetFilter)) {
            filterList.push(refinementElement.facetFilter);
          }
          if (filter.includes(refinementElement.facetFilter) && !refinementElement.selected) {
            refinementElement.selected = true;
          }
        });
        if (filterList.length > 0) {
          groupArray.push(`${catalog.name}:${filterList.join(SEARCH_RESULT_ITEM_JOIN_KEY)}`);
        }
      });
      setGroupArray(groupArray);
      setCatalogsState(catalogs);
    }
  }, [catalogs]);

  return (
    <div className="w-[235px] sm:w-full md:w-full">
      {catalogsState?.map((catalog) => (
        <CatalogAccordions
          key={catalog.name}
          catalog={catalog}
          groupArray={groupArray}
          onFiltersChange={onFiltersChange}
          onCatalogChange={onCatalogChange}
        />
      ))}
      {catalogsState.length > 0 && (
        <span>
          <div className="my-7 text-gray-900">{t('catalog.tips')}</div>
        </span>
      )}
    </div>
  );
}

function CatalogAccordions({ catalog, groupArray, onFiltersChange, onCatalogChange }: CatalogAccordionsProps) {
  const { t } = useLang(NAMESPACE.search);
  const { refinementElements = [], displayName = '' } = catalog;
  const [formatCatalog, setFormatCatalog] = useState([]);
  const [clickClearAll, setClickClearAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const firstShowItemNumber = 7;
  const displayShowAllButton = formatCatalog.length === firstShowItemNumber;
  const displayClearAll = refinementElements.find((category) => category.multiSelectOr === true);

  useEffect(() => {
    if (getSelectItemIndexInGroup() > firstShowItemNumber) {
      showAll();
    } else {
      showLess();
    }
  }, [refinementElements]);

  const getGroupAndItemInfo = () => {
    const currentGroupIndex = groupArray.findIndex((group) => group.includes(`${catalog.name}:`));
    const currentGroupString = groupArray[currentGroupIndex] || '';
    const currentGroupItems = currentGroupString?.split(SEARCH_RESULT_ITEM_JOIN_KEY).filter(Boolean);

    return {
      groupArray,
      currentGroupIndex,
      currentGroupString,
      currentGroupItems,
    };
  };

  const getSelectItemIndexInGroup = () => {
    let maxIndex = -1;
    const { currentGroupItems } = getGroupAndItemInfo();
    currentGroupItems.forEach((filter) => {
      const replaceFilter = filter.replace(`${catalog.name}:`, '');
      const index = refinementElements.findIndex((item) => item.facetFilter === replaceFilter);

      if (index > maxIndex) {
        maxIndex = index;
      }
    });
    return maxIndex;
  };

  const showAll = () => {
    setFormatCatalog(refinementElements);
  };

  const showLess = () => {
    const cloneDeepCategories = cloneDeep(refinementElements);
    setFormatCatalog(cloneDeepCategories.splice(0, firstShowItemNumber));
  };

  const clearAll = () => {
    const { groupArray, currentGroupIndex } = getGroupAndItemInfo();
    setClickClearAll(true);
    groupArray.splice(currentGroupIndex, 1);
    setParamsUrl(groupArray);
  };

  const setParamsUrl = (value: string[]) => {
    onFiltersChange(value.join(SEARCH_RESULT_GROUP_JOIN_KEY));
  };
  const checkIfExpanded = (value) => {
    setIsExpanded(value);
  };

  const selectedNumber = useMemo(() => {
    return refinementElements.filter((item) => item.selected).length;
  }, [refinementElements]);

  return (
    <Accordion
      defaultExpanded={catalog.expand}
      expandedIcon={<span className="icon-16-chevron-up-mono text-gray-500 pr-2" />}
      unExpandedIcon={<span className="icon-16-chevron-down-mono text-gray-500 pr-2" />}
      className="first:border-t-0"
      checkIfExpanded={checkIfExpanded}
    >
      <AccordionSummary>
        <p className="flex w-full">
          <span className="px-2">{displayName}</span>
          {selectedNumber > 0 && !isExpanded && (
            <span className="ml-auto w-5 h-5 bg-[#1e8ae7] text-[white] rounded-[50%] text-center mr-2 my-auto">
              {selectedNumber}
            </span>
          )}
        </p>
      </AccordionSummary>
      <AccordionDetails>
        <div className="flex flex-col">
          {formatCatalog.map((sub, index) => (
            <Catalog
              key={`${sub.facetFilter}_${sub.selected}`}
              sub={sub}
              parentName={catalog.name}
              setParamsUrl={setParamsUrl}
              onCatalogChange={onCatalogChange}
              clickClearAll={clickClearAll}
              setClickClearAll={setClickClearAll}
              groupAndItemInfo={getGroupAndItemInfo}
            />
          ))}
          {refinementElements.length > firstShowItemNumber && (
            <div className="flex">
              {displayShowAllButton && (
                <Button type="link" onClick={showAll} className="px-2">
                  +{t('show.all')}
                </Button>
              )}
              {!displayShowAllButton && (
                <Button type="link" onClick={showLess} className="px-2">
                  -{t('show.less')}
                </Button>
              )}
              <div className="flex-1" />
              {displayClearAll && (
                <Button type="link" onClick={clearAll} className="px-2">
                  {t('clear.all')}
                </Button>
              )}
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

function Catalog(props: CatalogProps) {
  const { setParamsUrl, parentName, sub, clickClearAll, setClickClearAll, groupAndItemInfo } = props;
  const [checked, setChecked] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setChecked(sub.selected);
  }, [sub.selected]);

  useEffect(() => {
    if (clickClearAll) {
      setChecked(false);
      setClickClearAll(false);
    }
  }, [clickClearAll]);

  const onSelectOption = (value: string) => {
    const replaceFilter = value.replace(`${parentName}:`, '');
    const { groupArray, currentGroupIndex, currentGroupString } = groupAndItemInfo();
    if (currentGroupIndex === -1) {
      groupArray.push(`${parentName}:${replaceFilter}`);
    } else {
      groupArray[currentGroupIndex] = currentGroupString + SEARCH_RESULT_ITEM_JOIN_KEY + replaceFilter;
    }
    setParamsUrl(groupArray);
  };

  const onRemoveOption = (value: string) => {
    const replaceFilter = value.replace(`${parentName}:`, '');
    const { groupArray, currentGroupIndex, currentGroupItems } = groupAndItemInfo();
    if (currentGroupIndex === -1) return;
    if (currentGroupItems.length === 1) {
      groupArray.splice(currentGroupIndex, 1);
      setParamsUrl(groupArray);
      return;
    }

    const removeIndex = currentGroupItems.findIndex((item) => item.replace(`${parentName}:`, '') === replaceFilter);
    currentGroupItems.splice(removeIndex, 1);

    if (removeIndex === 0) {
      currentGroupItems[0] = `${parentName}:${currentGroupItems[0]}`;
    }

    groupArray[currentGroupIndex] = currentGroupItems.join(SEARCH_RESULT_ITEM_JOIN_KEY);

    setParamsUrl(groupArray);
  };

  const onChangeCheckbox = (checked: boolean) => {
    setChecked(checked);
    if (checked) {
      onSelectOption(sub.facetFilter);
    } else {
      onRemoveOption(sub.facetFilter);
    }
  };

  if (sub.multiSelectOr) {
    return (
      <Checkbox
        className="mx-2 flex items-center"
        label={<span dangerouslySetInnerHTML={{ __html: `${sub.name} (${sub.count})` }}></span>}
        checked={checked}
        onChange={onChangeCheckbox}
      ></Checkbox>
    );
  }

  const redirectCategorySearch = () => {
    if (props.onCatalogChange) {
      props.onCatalogChange(sub.facetFilter);
    } else {
      router.push(`/search/category/${sub.categoryId}`);
    }
  };

  const categoryOption = () => {
    return (
      <div
        className="p-2 relative flex items-center cursor-pointer hover:bg-[#F1F9FF]"
        onClick={redirectCategorySearch}
      >
        <div className="flex-1">
          {sub.name}
          <span> ({sub.count})</span>
        </div>
        {sub.hasChild && <span className="icon-8-caret-right ml-2" style={{ transform: 'scale(0.75)' }} />}
      </div>
    );
  };
  if (sub.hasChild) {
    return (
      <Popover
        activeOpenClass="bg-[#F1F9FF]"
        withBorders
        trigger="hover"
        placement="right"
        customContent={
          <div className="flex flex-col bg-white border border-solid border-gray-500 shadow-md max-h-[290px] overflow-auto">
            {sub.childRefinementElements?.map((sub1, index) => (
              <Catalog
                key={index}
                sub={sub1}
                parentName={sub.name}
                groupAndItemInfo={groupAndItemInfo}
                setParamsUrl={setParamsUrl}
                onCatalogChange={props.onCatalogChange}
                clickClearAll={props.clickClearAll}
                setClickClearAll={props.setClickClearAll}
              />
            ))}
          </div>
        }
      >
        {categoryOption()}
      </Popover>
    );
  }

  return <>{categoryOption()}</>;
}
